import { logger } from '../utils/logger';

class OAuthService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  decodeJwt(token: string): any {
    if (!token) { return null; }
    const parts = token.split('.');
    if (parts.length !== 3) { return null; }
    try {
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(payload);
      return JSON.parse(decodeURIComponent(escape(decoded)));
    } catch {
      return null;
    }
  }

  /**
   * The custom Okta claim key that carries the phanes role value(s).
   * Must match the claim name configured in Okta for the phanes service.
   */
  private static readonly PHANES_ROLE_CLAIM = 'phanes-data-onboarding-role';

  /**
   * Extract role values from a decoded JWT payload.
   * Checks the phanes-specific claim first, then falls back to generic claim names.
   */
  private extractRolesFromPayload(payload: Record<string, unknown>): string[] | null {
    const claim = OAuthService.PHANES_ROLE_CLAIM;

    // 1. Phanes-specific claim (primary)
    if (Array.isArray(payload[claim])) { return payload[claim] as string[]; }
    if (typeof payload[claim] === 'string') { return [payload[claim] as string]; }

    // 2. Generic fallbacks (legacy / other environments)
    if (Array.isArray(payload.roles)) { return payload.roles as string[]; }
    if (Array.isArray(payload.role)) { return payload.role as string[]; }
    if (Array.isArray(payload.groups)) { return payload.groups as string[]; }
    if (typeof payload.roles === 'string') { return [payload.roles]; }
    if (typeof payload.role === 'string') { return [payload.role]; }

    return null;
  }

  /**
   * Extract user roles from the Okta PKCE token storage.
   * Looks for the phanes-specific claim "phanes-data-onboarding-role" first.
   * Returns string[] of roles, or [] if not found.
   */
  public getUserRoles(): string[] {
    try {
      const oktaStorage = localStorage.getItem('okta-token-storage');
      if (oktaStorage) {
        const parsed = JSON.parse(oktaStorage);

        // Try accessToken first (roles in access token)
        const accessTokenJwt = parsed?.accessToken?.accessToken;
        if (accessTokenJwt) {
          const payload = this.decodeJwt(accessTokenJwt);
          logger.debug('[getUserRoles] Okta PKCE access token decoded');
          if (payload) {
            const roles = this.extractRolesFromPayload(payload);
            if (roles) { return roles; }
          }
        }

        // Try idToken claims
        const idTokenClaims = parsed?.idToken?.claims;
        if (idTokenClaims) {
          logger.debug('[getUserRoles] Checking Okta ID token claims');
          const roles = this.extractRolesFromPayload(idTokenClaims);
          if (roles) { return roles; }
        }
      }
    } catch {
      // ignore
    }

    logger.warn('[getUserRoles] No roles found in any token storage');
    return [];
  }

  /**
   * Get the Okta PKCE access token string from okta-token-storage.
   * Used by api.ts to attach Bearer token to requests.
   */
  public getOktaAccessToken(): string | null {
    try {
      const oktaStorage = localStorage.getItem('okta-token-storage');
      if (!oktaStorage) { return null; }
      const parsed = JSON.parse(oktaStorage);
      return parsed?.accessToken?.accessToken ?? null;
    } catch {
      return null;
    }
  }
}

export const oauthService = new OAuthService();
export default oauthService;
